import React from "react";
import { Card, CardBody, CardTitle, CardText, Row, Col } from "reactstrap";
import './index.css'
import { ProductOverviewItem } from "../../../../redux/services/allocationService";

interface RestaurantSummaryProps {
  productOverviewData: ProductOverviewItem[];
}

export const RestaurantSummary: React.FC<RestaurantSummaryProps> = ({
  productOverviewData,
}) => {
  // Calculate totals from the actual data
  const totals = React.useMemo(() => {
    if (!productOverviewData || productOverviewData.length === 0) {
      return {
        totalProduced: 0,
        totalAllocated: 0,
        totalReserved: 0,
        totalCancelled: 0,
        totalAvailable: 0,
      };
    }

    const totalProduced = productOverviewData.reduce(
      (sum, item) => {
        const produced = item.quantities?.produced || item.quantities.produced || 0;
        return sum + produced;
      },
      0
    );
    
    const totalAllocated = productOverviewData.reduce(
      (sum, item) => {
        const allocated = item.quantities?.allocated || item.quantities.allocated || 0;
        return sum + allocated;
      },
      0
    );
    
    const totalReserved = productOverviewData.reduce(
      (sum, item) => {
        const reserved = item.quantities?.reserved || 0;
        return sum + reserved;
      },
      0
    );

    const totalCancelled = productOverviewData.reduce(
      (sum, item) => {
        const cancelled = item.quantities?.cancelled || 0;
        return sum + cancelled;
      },
      0
    );

    // Available = Produced - Allocated - Reserved + Cancelled
    const totalAvailable = totalProduced - totalAllocated - totalReserved + totalCancelled;

    return {
      totalProduced,
      totalAllocated,
      totalReserved,
      totalCancelled,
      totalAvailable,
    };
  }, [productOverviewData]);

  const summaryCards = [
    {
      title: "Total Produced",
      value: totals.totalProduced,
      subtitle: "units produced today",
      borderColor: "#4CAF50",
    },
    {
      title: "Total Allocated",
      value: totals.totalAllocated,
      subtitle: "allocated to carts",
      borderColor: "#2196F3",
    },
    {
      title: "Total Orders",
      value: totals.totalReserved,
      subtitle: "orders received (reserved)",
      borderColor: "#FF9800",
    },
    {
      title: "Cancelled Orders",
      value: totals.totalCancelled,
      subtitle: "cancelled orders",
      borderColor: "#9E9E9E",
      className: "text-secondary",
    },
    {
      title: "Net Available",
      value: totals.totalAvailable,
      subtitle: totals.totalAvailable >= 0 ? "surplus inventory" : "over-allocated",
      borderColor: totals.totalAvailable >= 0 ? "#4CAF50" : "#F44336",
      className: totals.totalAvailable >= 0 ? "text-success" : "text-danger",
    },
  ];

  return (
    <div className="restaurant-summary-container">
      <Row className="restaurant-inventory-summary-row mb-4">
        {summaryCards.map((card, index) => (
          <Col 
            key={index} 
            lg="auto" 
            md={3} 
            sm={4} 
            xs={12} 
            className="mb-3 d-flex justify-content-center"
          >
            <Card 
              className="inventory-summary-card h-100"
              style={{ 
                borderTop: `4px solid ${card.borderColor}`,
                maxWidth: '220px',
                width: '100%'
              }}
            >
              <CardBody className="inventory-summary-card-body text-center d-flex flex-column justify-content-center">
                <CardTitle tag="h6" className="inventory-summary-card-title text-muted mb-2">
                  {card.title}
                </CardTitle>
                <CardText 
                  tag="div" 
                  className={`inventory-summary-card-value fw-bold mb-2 ${card.className || ""}`}
                >
                  {card.value}
                </CardText>
                <small className="inventory-summary-card-subtitle text-muted mt-auto">
                  {card.subtitle}
                </small>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};